/**
 * Навигация сайдбара: список проектов и какой из них раскрыт.
 *
 * Данные придут из tracker API (DEV-1453 и далее) — здесь только форма и
 * состояние раскрытия, чтобы каркас не знал ни про транспорт, ни про права.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface NavProject {
  /** Ключ проекта в URL и в ключах задач: DEV, SALES, PROD */
  key: string
  /** Отображаемое имя без ключа: «Разработка» */
  name: string
  /** Цвет точки проекта; пустой — возьмётся цвет по умолчанию */
  color?: string
  /** Счётчик открытых задач; undefined — счётчик не показываем */
  openCount?: number
}

const EXPANDED_KEY = 'tracker-nav-expanded'

/** Счётчики верхней навигации; ключ — пункт меню. */
export type NavCounters = Partial<Record<'my' | 'projects', number>>

export const useNavigationStore = defineStore('navigation', () => {
  const projects = ref<NavProject[]>([])
  const counters = ref<NavCounters>({})
  const isLoading = ref(false)

  /**
   * Раскрытый проект помним между сессиями: сайдбар — единственный путь к
   * доске, и схлопывать его на каждый заход значит терять два клика.
   */
  const expandedKey = ref<string | null>(
    typeof window === 'undefined' ? null : window.localStorage.getItem(EXPANDED_KEY),
  )

  const expandedProject = computed(
    () => projects.value.find((project) => project.key === expandedKey.value) ?? null,
  )

  const setProjects = (next: NavProject[]) => {
    projects.value = next

    // Проект мог исчезнуть из выдачи — тогда раскрывать нечего.
    if (expandedKey.value && !next.some((project) => project.key === expandedKey.value)) {
      setExpanded(null)
    }
  }

  const setExpanded = (key: string | null) => {
    expandedKey.value = key

    if (typeof window === 'undefined') return

    if (key) window.localStorage.setItem(EXPANDED_KEY, key)
    else window.localStorage.removeItem(EXPANDED_KEY)
  }

  const toggleExpanded = (key: string) => {
    setExpanded(expandedKey.value === key ? null : key)
  }

  return {
    projects,
    counters,
    isLoading,
    expandedKey,
    expandedProject,
    setProjects,
    setExpanded,
    toggleExpanded,
  }
})
